/* global React, Icon, Button, t, toast */
// ============================================================
// KLIENTENS SIDE AV BYRÅOPPDRAGET
// ============================================================
// Et byrå kan be om tilgang til regnskapet, men får ingenting før noen som kan
// forvalte foretaket har akseptert. Denne porten er stedet det skjer.
//
// Den ligger i kundeappen, ikke i byråflaten, av samme grunn som at aksepten er
// klientens: byrået skal ikke kunne godkjenne seg selv inn. Backend håndhever det
// uansett — `acceptAccountingFirmEngagementHttp` krever eier eller admin i foretaket.

const FIRM_ENGAGEMENT_CHANGED = 'plumme-firm-engagement-changed';

function FirmEngagementGate({ backend }) {
  const businessId = backend?.activeBusinessId || '';
  const [pending, setPending] = React.useState([]);
  const [busy, setBusy] = React.useState('');

  const load = React.useCallback(async () => {
    if (!businessId) {
      setPending([]);
      return;
    }
    try {
      const entries = await window.PlummeBackend?.listBusinessFirmEngagements?.(businessId);
      setPending((entries || []).filter((entry) => entry.status === 'pending'));
    } catch (error) {
      // Reglene gir bare eier og admin lesetilgang. For alle andre er dette en
      // ikke-hendelse, ikke en feil aa vise.
      setPending([]);
    }
  }, [businessId]);

  React.useEffect(() => { void load(); }, [load]);

  // Den samme forespoerselen staar to steder: her, og under Innstillinger → Tilgang.
  // Svarer klienten ett sted, ma det andre folge med — ellers henger dette banneret
  // igjen og ber om tilgang som allerede er gitt.
  React.useEffect(() => {
    const sync = () => { void load(); };
    window.addEventListener(FIRM_ENGAGEMENT_CHANGED, sync);
    return () => window.removeEventListener(FIRM_ENGAGEMENT_CHANGED, sync);
  }, [load]);

  if (!backend?.user || !businessId || pending.length === 0) return null;
  const request = pending[0];

  const accept = async () => {
    setBusy('accept');
    try {
      await window.PlummeBackend?.acceptAccountingFirmEngagement?.({ firmId: request.firmId, businessId });
      toast(
        t('fe.accepted', '{firm} har nå tilgang til regnskapet.').replace('{firm}', request.firmName || t('fe.theFirm', 'Byrået')),
        'success',
      );
      await load();
      window.dispatchEvent(new CustomEvent(FIRM_ENGAGEMENT_CHANGED));
    } catch (error) {
      toast(error?.message || String(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const decline = async () => {
    setBusy('decline');
    try {
      await window.PlummeBackend?.declineAccountingFirmEngagement?.({ firmId: request.firmId, businessId });
      toast(t('fe.declined', 'Forespørselen er avslått.'), 'success');
      await load();
      window.dispatchEvent(new CustomEvent(FIRM_ENGAGEMENT_CHANGED));
    } catch (error) {
      toast(error?.message || String(error), 'error');
    } finally {
      setBusy('');
    }
  };

  return <aside className="household-invite-global" role="status">
    <span className="household-invite-global__icon"><Icon name="building" size={20} /></span>
    <div className="grow">
      <div className="t-h3">{t('fe.title', 'Et regnskapsbyrå ber om tilgang')}</div>
      <div className="t-sm muted">
        {(request.firmName || t('fe.theFirm', 'Byrået'))}
        {request.firmOrganizationNumber ? ' · Org. ' + request.firmOrganizationNumber : ''}
        {' · '}
        {request.billedTo === 'firm'
          ? t('fe.billedToFirm', 'Byrået betaler abonnementet')
          : t('fe.billedToClient', 'Du betaler abonnementet selv')}
      </div>
      <div className="t-xs muted" style={{ fontWeight: 600, marginTop: 4 }}>
        {t('fe.body', 'Godtar du, får medarbeiderne byrået setter på oppdraget tilgang til regnskapet ditt. Du kan avslutte samarbeidet når som helst.')}
      </div>
    </div>
    <div className="row gap-8">
      <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => void decline()}>
        {t('fe.decline', 'Avslå')}
      </Button>
      <Button size="sm" disabled={!!busy} onClick={() => void accept()}>
        {busy === 'accept' ? t('fe.accepting', 'Godtar …') : t('fe.accept', 'Gi tilgang')}
      </Button>
    </div>
  </aside>;
}

Object.assign(window, { FirmEngagementGate, FIRM_ENGAGEMENT_CHANGED });
